/**
 * @spec docs/specs/features/wallet-relation-api.md
 * @spec SEC-H003 Address Validation Standardization
 * Utility functions for wallet relations
 */

import { isAddress, getAddress } from 'ethers';
import type { CanonicalAddresses } from './types.js';

/**
 * Check if a string is a valid Ethereum address format
 *
 * Uses ethers isAddress() which properly handles:
 * - Lowercase addresses (0x...)
 * - Checksummed addresses (mixed case)
 * - Invalid formats
 *
 * @param address - String to validate
 * @returns true if valid address format
 */
export function isValidAddress(address: string): boolean {
  if (!address || typeof address !== 'string') {
    return false;
  }
  return isAddress(address);
}

/**
 * Validate and checksum an Ethereum address
 *
 * @param address - The address to validate
 * @returns The checksummed address
 * @throws Error if address is invalid
 */
export function validateAddress(address: string): string {
  try {
    return getAddress(address);
  } catch {
    throw new Error(`Invalid Ethereum address: ${address}`);
  }
}

/**
 * Sort two addresses into canonical order (lexicographic by lowercase).
 * This ensures that linking A↔B produces the same ordering as B↔A.
 *
 * @param addressA - First address
 * @param addressB - Second address
 * @returns Object with walletA (smaller) and walletB (larger)
 * @throws Error if addresses are identical (case-insensitive)
 *
 * @example
 * ```ts
 * const { walletA, walletB } = canonicalOrder(
 *   '0x9999...',
 *   '0x1111...'
 * );
 * // walletA = '0x1111...', walletB = '0x9999...'
 * ```
 */
export function canonicalOrder(addressA: string, addressB: string): CanonicalAddresses {
  const lowerA = addressA.toLowerCase();
  const lowerB = addressB.toLowerCase();

  if (lowerA === lowerB) {
    throw new Error('Cannot link a wallet to itself');
  }

  return lowerA < lowerB
    ? { walletA: addressA, walletB: addressB }
    : { walletA: addressB, walletB: addressA };
}
